/**
 * Program rule sets — V102-6-0 / ADR-0058 §1–2, D-102-17.
 *
 * Rule *values* for a scored program (CMMC points, partial cases, the 88
 * threshold, POA&M eligibility, the never-POA&M set, the closeout clock,
 * the affirmation cadence) are versioned, reviewed content, never code:
 *   - they arrive as a `program_rule_set` proposal (migration 266b) and are
 *     written only by `applyProgramRuleSetChange` (validated: the program
 *     exists, its manifest scores with this family, the JSON passes the
 *     program's zod schema, the version is the previous one + 1);
 *   - one version per program is `approved`; a new one supersedes it. Rows
 *     are never edited or deleted (triggers, migration 266a);
 *   - a period pins its rule set at Prepare → Audit (`audit_periods.rule_set_id`,
 *     written by `startProgramPeriodFieldwork`). Before Audit, scoring uses the
 *     current approved set and says so (`basis: 'current'`).
 * A Task Force change is therefore a new proposal approved in the queue that
 * applies from the next period, with no deploy.
 *
 * Planes: program_rule_sets is control plane (`contentDb`); the period pin is
 * tenant data (`db`).
 */
import { z } from 'zod';
import { getProgramManifest, PROGRAM_MANIFESTS } from '../programs/manifests.js';
import { canonicalJson, sha256Hex } from '../utils/audit-crypto.js';
import { ContentValidationError } from './framework-requirement-service.js';
import { GOVRAMP_RULE_FAMILY, GovrampRulesV1 } from './govramp-program-rules.js';
import { FEDRAMP_READINESS_FAMILY, FedrampReadinessRulesV1 } from './fedramp-readiness-rules.js';
import { SOC2_READINESS_FAMILY, Soc2ReadinessRulesV1 } from './soc2-readiness-rules.js';
import { ISO_CERTIFICATION_FAMILY, IsoCertificationRulesV1 } from './iso-certification-rules.js';
// ---- CMMC rules v1 schema (ADR-0058 §1) ---------------------------------------------
const L2_ID = /^cmmc-l2:3\.\d{1,2}\.\d{1,2}$/;
const L1_ID = /^cmmc-l1:[A-Z]{2}\.L1-b\.1\.[ivx]+$/;
const CONDITION = /^[a-z][a-z0-9_]{2,63}$/;
const text = z.string().trim().min(1).max(500);
const PartialCase = z.object({
    condition: z.string().regex(CONDITION),
    points: z.number().int().min(0).max(5),
    label: text,
}).strict();
const CmmcRequirementRule = z.object({
    id: z.string().regex(L2_ID),
    /** null only for the SSP requirement (3.12.4): no points, but it must exist. */
    points: z.union([z.literal(1), z.literal(3), z.literal(5), z.null()]),
    /** `yes` (POA&M-eligible), `no`, or `exception` (eligible only under a named partial case). */
    poam_eligible: z.enum(['yes', 'no', 'exception']),
    never_poam: z.boolean(),
    source_section: text,
    partial: z.array(PartialCase).min(1).max(4).optional(),
    ssp: z.literal(true).optional(),
}).strict();
export const CmmcRulesV1 = z.object({
    schema: z.literal('gacpro.program-rules.cmmc/1'),
    program_key: z.literal('cmmc'),
    levels: z.object({
        L1: z.object({
            framework_id: z.literal('cmmc-l1'),
            scoring: z.literal('all_met'),
            poam: z.literal(false),
            source_section: text,
            requirements: z.array(z.object({
                id: z.string().regex(L1_ID),
                far_clause: text,
                l2: z.array(z.string().regex(L2_ID)).min(1).max(5),
            }).strict()).min(1).max(30),
        }).strict(),
        L2: z.object({
            framework_id: z.literal('cmmc-l2'),
            scoring: z.literal('points'),
            requirement_count: z.number().int().min(1),
            source_section: text,
        }).strict(),
    }).strict(),
    max_score: z.number().int().min(1),
    minimum: z.object({ value: z.number().int(), derived: z.boolean(), derivation: text }).strict(),
    conditional_threshold: z.number().int(),
    na_counts_as_met: z.literal(true),
    requirements: z.array(CmmcRequirementRule).min(1).max(200),
    ssp_requirement: z.object({
        requirement: z.string().regex(L2_ID),
        points: z.null(),
        must_exist: z.literal(true),
        source_section: text,
    }).strict(),
    poam: z.object({
        eligible_points: z.number().int().min(0).max(5),
        exceptions: z.array(z.object({ requirement: z.string().regex(L2_ID), when_condition: z.string().regex(CONDITION) }).strict()).max(10),
        never_eligible: z.array(z.string().regex(L2_ID)).max(50),
        closeout_days: z.number().int().min(1).max(730),
        source_section: text,
    }).strict(),
    affirmation: z.object({
        cadence_months: z.number().int().min(1).max(36),
        on_certification: z.boolean(),
        on_poam_closeout: z.boolean(),
        source_section: text,
    }).strict(),
    outcomes: z.object({ L1: z.string().min(1).max(100), L2: z.string().min(1).max(100) }).strict(),
    /** ADR-0059 §5 / V102-7-5 M1: C3PAO independence lookback (32 CFR 170.8(b)(17)(ii)(G)); absent = 36. Content-only tightening. */
    independence_lookback_months: z.number().int().min(12).max(120).optional(),
}).strict().superRefine((r, ctx) => {
    const issue = (message) => ctx.addIssue({ code: 'custom', message });
    const ids = new Set();
    let total = 0;
    for (const q of r.requirements) {
        if (ids.has(q.id))
            issue(`duplicate requirement ${q.id}`);
        ids.add(q.id);
        total += q.points ?? 0;
        if ((q.points === null) !== (q.ssp === true))
            issue(`${q.id}: only the SSP requirement has no points`);
        if (q.never_poam && q.poam_eligible !== 'no')
            issue(`${q.id}: a never-POA&M requirement cannot be POA&M-eligible`);
        if (q.poam_eligible === 'yes' && q.points !== r.poam.eligible_points)
            issue(`${q.id}: only ${r.poam.eligible_points}-point requirements are POA&M-eligible`);
        if (q.partial) {
            if (q.points === null)
                issue(`${q.id}: the SSP requirement has no partial cases`);
            for (const p of q.partial)
                if (q.points !== null && p.points > q.points)
                    issue(`${q.id}: partial case ${p.condition} deducts more than the requirement`);
        }
        if (q.poam_eligible === 'exception' && !r.poam.exceptions.some((e) => e.requirement === q.id)) {
            issue(`${q.id}: an exception requirement needs a poam.exceptions entry`);
        }
    }
    if (r.requirements.length !== r.levels.L2.requirement_count)
        issue(`levels.L2.requirement_count is ${r.levels.L2.requirement_count} but ${r.requirements.length} requirements are listed`);
    const ssp = r.requirements.filter((q) => q.ssp);
    if (ssp.length !== 1 || ssp[0].id !== r.ssp_requirement.requirement)
        issue('exactly one requirement is the SSP requirement, and ssp_requirement names it');
    for (const e of r.poam.exceptions) {
        const q = r.requirements.find((x) => x.id === e.requirement);
        if (!q || q.poam_eligible !== 'exception')
            issue(`poam exception ${e.requirement} must be an 'exception' requirement`);
        else if (!q.partial?.some((p) => p.condition === e.when_condition))
            issue(`poam exception ${e.requirement} names unknown condition ${e.when_condition}`);
    }
    const never = new Set(r.poam.never_eligible);
    for (const q of r.requirements)
        if (q.never_poam !== never.has(q.id))
            issue(`${q.id}: never_poam disagrees with poam.never_eligible`);
    for (const id of never)
        if (!ids.has(id))
            issue(`poam.never_eligible names unknown requirement ${id}`);
    for (const l1 of r.levels.L1.requirements)
        for (const l2 of l1.l2)
            if (!ids.has(l2))
                issue(`${l1.id} maps to unknown requirement ${l2}`);
    if (r.conditional_threshold > r.max_score)
        issue('conditional_threshold cannot exceed max_score');
    if (r.minimum.derived && r.minimum.value !== r.max_score - total)
        issue(`a derived minimum must equal max_score − Σ points (${r.max_score - total})`);
});
export const CMMC_RULE_FAMILY = 'cmmc-program-rules';
const RULE_SET_FAMILIES = new Map();
const RULE_SET_ENGINE_OF = new Map();
/** Default switch reading (ADR-0068 §4): the parsed rules' `engine` field. */
const engineField = (rules) => {
    const e = rules?.engine;
    return typeof e === 'string' ? e : null;
};
/**
 * Registers the zod schema for a rule-set family (a manifest's `scoring.rulesRef`
 * or `readiness.switchRuleSet`). A family registers once. A switch family may
 * say how its parsed rules name the engine (`engineOf`, ADR-0079 §8); the
 * default reads `rules.engine`.
 */
export function registerRuleSetFamily(family, schema, opts = {}) {
    if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(family))
        throw new Error(`rule-set family ${family} must be lower-case kebab`);
    if (RULE_SET_FAMILIES.has(family))
        throw new Error(`rule-set family ${family} is already registered`);
    RULE_SET_FAMILIES.set(family, schema);
    RULE_SET_ENGINE_OF.set(family, opts.engineOf ?? engineField);
}
registerRuleSetFamily(CMMC_RULE_FAMILY, CmmcRulesV1);
registerRuleSetFamily(GOVRAMP_RULE_FAMILY, GovrampRulesV1);
registerRuleSetFamily(FEDRAMP_READINESS_FAMILY, FedrampReadinessRulesV1);
// ADR-0078 §4 / ADR-0079 §8: the second switch family, `{ engine: 'objective' | 'framework' }`.
registerRuleSetFamily(SOC2_READINESS_FAMILY, Soc2ReadinessRulesV1);
// ADR-0080 §5: the ISO certification cycle (no engine field; it never switches readiness).
registerRuleSetFamily(ISO_CERTIFICATION_FAMILY, IsoCertificationRulesV1, { engineOf: () => null });
/** The rule family a program reads: its scoring rules, else its readiness switch (ADR-0068 §4). One per program. */
export function programRuleFamily(programKey) {
    const m = getProgramManifest(programKey);
    return m?.scoring.rulesRef ?? m?.readiness.switchRuleSet ?? m?.certificationCycle?.rulesRef ?? null;
}
/** The validator per program, by the family its manifest declares. */
export const PROGRAM_RULE_SCHEMAS = Object.freeze(Object.fromEntries(PROGRAM_MANIFESTS.flatMap((m) => {
    const family = m.scoring.rulesRef ?? m.readiness.switchRuleSet ?? m.certificationCycle?.rulesRef;
    const schema = family ? RULE_SET_FAMILIES.get(family) : undefined;
    return schema ? [[m.key, schema]] : [];
})));
export class ProgramRuleSetError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'ProgramRuleSetError';
    }
}
export function ruleSetIdFor(family, version) {
    return `${family}@v${version}`;
}
export async function rulesSha256(rules) {
    return sha256Hex(canonicalJson(rules));
}
export async function getProgramRuleSet(content, ruleSetId) {
    const r = await content.query(`SELECT * FROM program_rule_sets WHERE rule_set_id = ? LIMIT 1`, [ruleSetId]);
    return r.results[0] ?? null;
}
/** The program's current approved rule set (null before one is approved). */
export async function getCurrentProgramRuleSet(content, programKey) {
    const r = await content.query(`SELECT * FROM program_rule_sets WHERE program_key = ? AND status = 'approved' ORDER BY version DESC LIMIT 1`, [programKey]);
    return r.results[0] ?? null;
}
export async function listProgramRuleSets(content, programKey) {
    const r = await content.query(`SELECT * FROM program_rule_sets WHERE program_key = ? ORDER BY version`, [programKey]);
    return r.results;
}
/** Recompute the hash from the stored JSON; true when it still matches. */
export async function verifyProgramRuleSet(rs) {
    return (await rulesSha256(JSON.parse(rs.rules_json))) === rs.content_sha256;
}
/**
 * The rule set that judges a period: its pin once in Audit, else the
 * program's current approved set. A pin whose stored content no longer
 * matches its hash is refused.
 */
export async function resolveRuleSetForPeriod(db, input) {
    const content = input.contentDb ?? db;
    const p = await db.query(`SELECT rule_set_id FROM audit_periods WHERE period_id = ? AND workspace_id = ? LIMIT 1`, [input.periodId, input.workspaceId]);
    const row = p.results[0];
    if (!row)
        throw new ProgramRuleSetError('not_found', 'Audit period not found.');
    if (row.rule_set_id) {
        const rs = await getProgramRuleSet(content, row.rule_set_id);
        if (!rs || rs.program_key !== input.programKey)
            throw new ProgramRuleSetError('conflict', `Pinned rule set ${row.rule_set_id} is missing.`);
        if (!(await verifyProgramRuleSet(rs)))
            throw new ProgramRuleSetError('conflict', `Pinned rule set ${row.rule_set_id} does not match its recorded hash.`);
        return { ruleSet: rs, basis: 'pinned' };
    }
    const cur = await getCurrentProgramRuleSet(content, input.programKey);
    return cur ? { ruleSet: cur, basis: 'current' } : null;
}
/** Parse + validate a stored CMMC rule set. */
export function parseCmmcRules(rs) {
    const parsed = CmmcRulesV1.safeParse(JSON.parse(rs.rules_json));
    if (!parsed.success)
        throw new ProgramRuleSetError('invalid', `Rule set ${rs.rule_set_id} is not valid CMMC rules: ${parsed.error.issues[0]?.message}`);
    return parsed.data;
}
/** Parse + validate a stored GovRAMP rule set. */
export function parseGovrampRules(rs) {
    const parsed = GovrampRulesV1.safeParse(JSON.parse(rs.rules_json));
    if (!parsed.success)
        throw new ProgramRuleSetError('invalid', `Rule set ${rs.rule_set_id} is not valid GovRAMP rules: ${parsed.error.issues[0]?.message}`);
    return parsed.data;
}
/** Parse + validate a stored rule set against its family's schema. */
export function parseRuleSet(rs) {
    const schema = RULE_SET_FAMILIES.get(rs.family);
    if (!schema)
        throw new ProgramRuleSetError('invalid', `Rule set ${rs.rule_set_id} has unknown family ${rs.family}.`);
    const parsed = schema.safeParse(JSON.parse(rs.rules_json));
    if (!parsed.success)
        throw new ProgramRuleSetError('invalid', `Rule set ${rs.rule_set_id} is not valid ${rs.family} rules: ${parsed.error.issues[0]?.message}`);
    return parsed.data;
}
// ---- proposal applier (ADR-0058 §1) -----------------------------------------------------
const str = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);
/**
 * Validated writer for `program_rule_set` proposals. proposed_state:
 * `{ program_key, family, version, rules_json, source_url?, source_version?, effective_from }`.
 * Only `create`: a rule set is never edited or deprecated, a change is the
 * next version. Writes nothing unless every check passes.
 */
export async function applyProgramRuleSetChange(db, change, reviewer) {
    if (!reviewer)
        throw new ContentValidationError('program_rule_set content needs a reviewer (provenance is mandatory)');
    if (!change.source_kind)
        throw new ContentValidationError('program_rule_set content needs a source_kind');
    if (change.change_type !== 'create') {
        throw new ContentValidationError('a program rule set is never edited or deprecated; propose the next version');
    }
    const raw = typeof change.proposed_state === 'string' ? JSON.parse(change.proposed_state) : change.proposed_state;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw))
        throw new ContentValidationError('proposed_state must be an object');
    const p = raw;
    const programKey = str(p.program_key);
    const family = str(p.family);
    const effectiveFrom = str(p.effective_from);
    if (!programKey || !family || !effectiveFrom)
        throw new ContentValidationError('program_rule_set needs program_key, family and effective_from');
    if (!/^\d{4}-\d{2}-\d{2}/.test(effectiveFrom))
        throw new ContentValidationError('effective_from must be a date (YYYY-MM-DD)');
    const prog = await db.query(`SELECT 1 AS ok FROM programs WHERE program_key = ? LIMIT 1`, [programKey]);
    if (!prog.results.length)
        throw new ContentValidationError(`unknown program ${programKey}`);
    const manifest = getProgramManifest(programKey);
    const programFamily = programRuleFamily(programKey);
    if (!manifest || !programFamily)
        throw new ContentValidationError(`program ${programKey} has no scoring engine or rule family, so it takes no rule set`);
    if (programFamily !== family)
        throw new ContentValidationError(`program ${programKey} reads rule family ${programFamily}, not ${family}`);
    const schema = RULE_SET_FAMILIES.get(family);
    if (!schema)
        throw new ContentValidationError(`unknown rule family ${family}`);
    const rules = typeof p.rules_json === 'string' ? JSON.parse(p.rules_json) : p.rules_json;
    const parsed = schema.safeParse(rules);
    if (!parsed.success) {
        const first = parsed.error.issues[0];
        throw new ContentValidationError(`rules_json is invalid: ${first?.path?.join('.') || '(root)'} ${first?.message ?? ''}`.trim());
    }
    const max = await db.query(`SELECT MAX(version) AS v FROM program_rule_sets WHERE program_key = ?`, [programKey]);
    const next = (max.results[0]?.v ?? 0) + 1;
    if (p.version !== undefined && p.version !== next)
        throw new ContentValidationError(`version must be ${next} (the previous version + 1)`);
    const id = ruleSetIdFor(family, next);
    if (change.target_id && change.target_id !== id)
        throw new ContentValidationError(`target_id must be '${id}'`);
    const rulesJson = canonicalJson(parsed.data);
    const sha = await sha256Hex(rulesJson);
    const reviewedAt = change.reviewed_at ?? new Date().toISOString();
    const now = new Date().toISOString();
    await db.batch([
        {
            sql: `UPDATE program_rule_sets SET status = 'superseded', superseded_at = ? WHERE program_key = ? AND status = 'approved'`,
            params: [now, programKey],
        },
        {
            sql: `INSERT INTO program_rule_sets (
              rule_set_id, program_key, family, version, rules_json, content_sha256, source_url, source_version,
              effective_from, status, source_kind, reviewer, reviewed_at, change_id, created_at
            ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, 'approved', ?, ?, ?, ?, ?)`,
            params: [id, programKey, family, next, rulesJson, sha, str(p.source_url), str(p.source_version), effectiveFrom,
                change.source_kind, reviewer, reviewedAt, change.change_id, now],
        },
    ]);
    return id;
}
/**
 * The readiness engine a program runs now. A program with a switch reads its
 * current approved rule set of that family; none approved → the legacy engine.
 * A row whose stored content no longer matches its hash is refused.
 */
export async function resolveReadinessEngine(content, programKey) {
    const m = getProgramManifest(programKey);
    if (!m)
        throw new ProgramRuleSetError('not_found', `Unknown program ${programKey}.`);
    const family = m.readiness.switchRuleSet;
    if (!family || !m.readiness.legacyEngine)
        return { engine: m.readiness.engine, basis: 'manifest', rule_set_id: null, version: null, rules: null };
    const cur = await getCurrentProgramRuleSet(content, programKey);
    if (!cur || cur.family !== family)
        return { engine: m.readiness.legacyEngine, basis: 'legacy', rule_set_id: null, version: null, rules: null };
    if (!(await verifyProgramRuleSet(cur)))
        throw new ProgramRuleSetError('conflict', `Rule set ${cur.rule_set_id} does not match its recorded hash.`);
    // ADR-0079 §8: each family parses its own rules and says which engine they name.
    const rules = parseRuleSet(cur);
    const engine = (RULE_SET_ENGINE_OF.get(family) ?? engineField)(rules);
    if (!engine || (engine !== m.readiness.engine && engine !== m.readiness.legacyEngine)) {
        throw new ProgramRuleSetError('invalid', `Rule set ${cur.rule_set_id} names engine ${String(engine)}; ${programKey} switches between ${m.readiness.engine} and ${m.readiness.legacyEngine}.`);
    }
    return { engine, basis: 'rule_set', rule_set_id: cur.rule_set_id, version: cur.version, rules: rules };
}
/**
 * ADR-0079 §8: for consumers that call the `framework` engine directly (the
 * framework readiness route, the mock audit, the SOC 2 freeze). Returns the
 * resolved engine when the program's switch has the legacy engine
 * `framework` and its approved rule set names another engine; null means
 * "keep the framework path". Programs that never ran on the framework engine
 * through a switch (CMMC and GovRAMP by manifest, FedRAMP with legacy `ksi`)
 * keep their existing consumers unchanged.
 */
/**
 * The objective mode a program's consumers should use now (ADR-0078 §4
 * amendment, V106-1-12): a program whose switch has the legacy engine
 * `framework` and whose switch is not approved keeps its pre-switch
 * requirement-level view (`single`: every criterion, no applicability
 * filter), so content filed ahead of the switch (points of focus, the
 * categories option) changes no customer-facing list until the owner
 * approves the switch. Every other program: the manifest's mode.
 */
export async function effectiveObjectiveMode(content, programKey) {
    const m = getProgramManifest(programKey);
    if (!m)
        return null;
    if (m.readiness.legacyEngine === 'framework' && m.readiness.switchRuleSet) {
        const switched = await frameworkEngineSwitchedOff(content, programKey).catch(() => null);
        if (!switched)
            return 'single';
    }
    return m.readiness.objectiveMode;
}
export async function frameworkEngineSwitchedOff(content, programKey) {
    const m = getProgramManifest(programKey);
    if (!m || m.readiness.legacyEngine !== 'framework' || !m.readiness.switchRuleSet)
        return null;
    const r = await resolveReadinessEngine(content, programKey);
    return r.engine === 'framework' ? null : r;
}
//# sourceMappingURL=program-rule-set-service.js.map